import type { CandleWithFlag } from "features/chart";
import type { StochasticMAType } from "./stochasticOscillatorSettings";

export interface StochasticLines {
  k: (number | null)[];
  d: (number | null)[];
}

function applyMA(values: (number | null)[], period: number, type: StochasticMAType): (number | null)[] {
  const n = values.length;
  const out: (number | null)[] = new Array(n).fill(null);
  if (type === "SMA" || type === "TMA") {
    const sma: (number | null)[] = new Array(n).fill(null);
    let sum = 0, win: number[] = [];
    for (let i = 0; i < n; i++) {
      const v = values[i]; if (v === null) continue;
      win.push(v); sum += v;
      if (win.length > period) sum -= win.shift()!;
      if (win.length === period) sma[i] = sum / period;
    }
    if (type === "SMA") return sma;
    const p2 = Math.floor(period / 2) + 1;
    let s2 = 0, w2: number[] = [];
    for (let i = 0; i < n; i++) {
      const v = sma[i]; if (v === null) continue;
      w2.push(v); s2 += v;
      if (w2.length > p2) s2 -= w2.shift()!;
      if (w2.length === p2) out[i] = s2 / p2;
    }
    return out;
  }
  if (type === "WMA") {
    const denom = (period * (period + 1)) / 2;
    const win: number[] = [];
    for (let i = 0; i < n; i++) {
      const v = values[i]; if (v === null) continue;
      win.push(v);
      if (win.length > period) win.shift();
      if (win.length === period) {
        out[i] = win.reduce((s, x, j) => s + x * (j + 1), 0) / denom;
      }
    }
    return out;
  }
  // EMA and SMMA
  const k = type === "EMA" ? 2 / (period + 1) : 1 / period;
  let prev: number | null = null, seedSum = 0, seedCount = 0;
  for (let i = 0; i < n; i++) {
    const v = values[i]; if (v === null) continue;
    if (prev === null) {
      seedSum += v; seedCount++;
      if (seedCount === period) { prev = seedSum / period; out[i] = prev; }
    } else {
      prev = v * k + prev * (1 - k); out[i] = prev;
    }
  }
  return out;
}

export function calcStochastic(
  data: CandleWithFlag[],
  kPeriod: number,
  smoothingPeriod: number,
  dPeriod: number,
  maType: StochasticMAType,
): StochasticLines {
  const n = data.length;
  const rawK: (number | null)[] = new Array(n).fill(null);

  for (let i = kPeriod - 1; i < n; i++) {
    if (!("high" in data[i])) continue;
    let lo = Infinity, hi = -Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      if (data[j].high > hi) hi = data[j].high;
      if (data[j].low < lo) lo = data[j].low;
    }
    rawK[i] = hi === lo ? 0 : ((data[i].close - lo) / (hi - lo)) * 100;
  }

  const k = applyMA(rawK, smoothingPeriod, maType);
  const d = applyMA(k, dPeriod, maType);
  return { k, d };
}
